/**
 * The crypto payment block of a concierge request (CONCIERGE_PAYMENTS_MODE.md §6.4.1 «Сроки для клиента», §6.4.4
 * rule 4 and «FORWARD в пути», §11.1 «Текущая оплата, крипта») — pure, so every phase → copy/buttons pair is decided
 * and tested in one place. Driven ONLY by `payment.wallet` (the server's ClientWalletView) plus the payment's own
 * server flags; nothing is guessed from the status.
 */
import type { ClientWalletView, PaymentClientView } from '@/api/concierge';

/** Phases in which «Вернуть» (refund before the forward) may be offered — and only when the server says so. */
const REFUND_PHASES = new Set(['AWAITING', 'EXPIRED', 'HOLD']);

/** Parameters of a title: an amount with its asset, and an ISO instant rendered by the screen. */
export interface CryptoTitleParams {
  amount?: number | string;
  asset?: string;
  date?: string;
}

export interface CryptoBlock {
  phase: string;
  /** The plate's title (i18n key), or null — nothing to say (an unknown phase, or REFUND already said by the status). */
  titleKey: string | null;
  titleParams: CryptoTitleParams;
  bodyKey: string | null;
  /** «К оплате N USDT, в т.ч. комиссия M%» — the frozen total and fee; null until frozen. */
  ask: { amount: number | string; asset: string; fee: number | string | null; feePercent: number | string | null } | null;
  /** «Оплатить до» — quoteValidUntil, only while the client is asked to pay. */
  deadline: string | null;
  /** The address to show with its QR — only while the client is asked to pay; null hides the call to send money. */
  address: string | null;
  /** «Получено N» — what arrived so far; null when nothing did. */
  received: number | string | null;
  /** «Получить новый расчёт». */
  requote: boolean;
  /** «Вернуть» (before the forward) — opens the refund form. */
  refundRequest: boolean;
  /** A refund was asked (before the forward, or from the provider) — the «запрос отправлен» line. */
  refundRequested: boolean;
}

function positive(v: number | string | null | undefined): boolean {
  const n = Number(v);
  return v != null && Number.isFinite(n) && n > 0;
}

/**
 * The block for the current crypto payment, or null when the payment carries no wallet view (bank/cash).
 * `requestStatus` — the request's shown status: REFUND_PENDING already says «Средства возвращаются» on the
 * status plate, so the wallet does not repeat it.
 */
export function cryptoBlock(p: PaymentClientView, requestStatus?: string | null): CryptoBlock | null {
  const w: ClientWalletView | null = p.wallet;
  if (!w) return null;
  const asset = w.asset || 'USDT';
  const ask = w.askAmount != null
    ? { amount: w.askAmount, asset, fee: w.feeAmount ?? null, feePercent: p.feePercent ?? null }
    : null;
  const received = positive(w.received) ? w.received : null;
  const owes = positive(w.remainingToPay);
  const block: CryptoBlock = {
    phase: w.phase,
    titleKey: null,
    titleParams: {},
    bodyKey: null,
    ask,
    deadline: null,
    address: null,
    received,
    requote: w.phase === 'EXPIRED' && w.canRequote,
    refundRequest: REFUND_PHASES.has(w.phase) && w.canRefundRequest && !w.refundRequested,
    refundRequested: w.phase !== 'REFUND'
      && (w.refundRequested || (!p.refundRequestable && !!p.refundRequestedAt)),
  };

  switch (w.phase) {
    case 'WAITING_QUOTE':
    case 'PREPARING':
      block.titleKey = 'vitrine.concierge.payment.cryptoPreparing';
      break;
    case 'RECALCULATING':
      // The provider's address was revoked: a new quote is coming; no buttons until it does (§6.4.1, D22).
      block.titleKey = 'vitrine.concierge.wallet.recalculating';
      block.bodyKey = 'vitrine.concierge.wallet.recalculatingBody';
      break;
    case 'AWAITING':
      if (received && !owes) {
        // Everything arrived, the forward has not started yet — never invite a second transfer.
        block.titleKey = 'vitrine.concierge.wallet.hold';
        break;
      }
      block.address = w.address;
      block.deadline = w.quoteValidUntil;
      if (!w.address) {
        block.titleKey = 'vitrine.concierge.payment.cryptoPreparing';
      } else if (received && owes) {
        // §6.4.1: «доплатите N на тот же адрес до …»
        block.titleKey = 'vitrine.concierge.wallet.topUp';
        block.titleParams = { amount: w.remainingToPay ?? undefined, asset, date: w.quoteValidUntil ?? undefined };
      } else if (ask) {
        block.titleKey = 'vitrine.concierge.wallet.send';
        block.titleParams = { amount: ask.amount, asset };
      }
      break;
    case 'CHECKING':
      // §6.4.4 rule 4: the deadline passed, arrivals still count until acceptUntil — no buttons.
      block.titleKey = 'vitrine.concierge.wallet.checking';
      block.titleParams = { date: w.acceptUntil ?? undefined };
      break;
    case 'EXPIRED':
      block.titleKey = 'vitrine.concierge.wallet.expired';
      if (block.requote) {
        block.bodyKey = block.refundRequest ? 'vitrine.concierge.wallet.expiredBodyRefund' : 'vitrine.concierge.wallet.expiredBody';
      } else if (w.requoteRequested && !block.refundRequested) {
        // a new quote was already asked (the server says so — canRequote is false for other reasons too)
        block.bodyKey = 'vitrine.concierge.wallet.requoteRequested';
      }
      break;
    case 'HOLD':
      block.titleKey = 'vitrine.concierge.wallet.hold';
      break;
    case 'FORWARDING':
      // §6.4.4 «FORWARD в пути»: no refund action of either kind until the forward settles.
      block.titleKey = 'vitrine.concierge.wallet.forwarding';
      break;
    case 'AT_TH':
      // the provider holds the money: «Запросить возврат» comes from payment.refundRequestable (paymentActions)
      block.titleKey = 'vitrine.concierge.wallet.atTh';
      break;
    case 'REFUND':
      block.titleKey = requestStatus === 'REFUND_PENDING' ? null : 'vitrine.concierge.state.refundPending';
      break;
    default:
      // a phase this build does not know: no address, no buttons, no guessed words
      block.requote = false;
      block.refundRequest = false;
      break;
  }
  return block;
}
